import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";
import { AppNav } from "@/components/AppNav";
import { useSession } from "@/hooks/useSession";

function NotFoundComponent() {
  return (
    <main className="min-h-screen text-foreground">
      <AppNav maxWidth="max-w-6xl" />
      <div className="mx-auto flex max-w-3xl flex-col items-center justify-center px-6 py-20 text-center sm:py-32">
        <p className="text-[11px] font-medium uppercase tracking-[0.25em] text-primary">Erro 404</p>
        <h1 className="mt-4 font-display text-5xl leading-[0.95] sm:text-6xl">
          <span className="text-iridescent">Página não encontrada</span>
        </h1>
        <p className="mx-auto mt-6 max-w-lg text-base text-muted-foreground sm:text-lg">
          O link pode estar quebrado ou apontar pra uma versão antiga do site. Volte pra home ou explore os anúncios disponíveis.
        </p>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/"
            className="rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition hover:opacity-90"
          >
            Ir pra home
          </Link>
          <Link
            to="/buscar"
            className="chip rounded-full px-6 py-3 text-sm font-medium text-foreground transition dark:hover:bg-white/10 hover:bg-black/10"
          >
            Ver anúncios
          </Link>
        </div>
      </div>
    </main>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

const _SEO_MODELOS = [
  { slug: "jeep-compass",                label: "Jeep Compass Blindado" },
  { slug: "toyota-corolla",              label: "Toyota Corolla Blindado" },
  { slug: "toyota-corolla-cross",        label: "Corolla Cross Blindado" },
  { slug: "jeep-commander",              label: "Jeep Commander Blindado" },
  { slug: "toyota-hilux",               label: "Toyota Hilux Blindada" },
  { slug: "bmw-320i",                   label: "BMW 320i Blindada" },
  { slug: "bmw-x1",                     label: "BMW X1 Blindado" },
  { slug: "vw-tiguan",                  label: "VW Tiguan Blindado" },
  { slug: "audi-q5",                    label: "Audi Q5 Blindado" },
  { slug: "land-rover-discovery-sport",  label: "Discovery Sport Blindado" },
  { slug: "land-rover-range-rover",     label: "Range Rover Blindado" },
  { slug: "volvo-xc90",                 label: "Volvo XC90 Blindado" },
  { slug: "gwm-haval-h6",              label: "GWM Haval H6 Blindado" },
  { slug: "byd-song-plus",             label: "BYD Song Plus Blindado" },
  { slug: "byd-seal",                  label: "BYD Seal Blindado" },
];

const _SEO_MARCAS = [
  { slug: "toyota",        label: "Toyota Blindado" },
  { slug: "jeep",          label: "Jeep Blindado" },
  { slug: "volkswagen",    label: "Volkswagen Blindado" },
  { slug: "bmw",           label: "BMW Blindado" },
  { slug: "audi",          label: "Audi Blindado" },
  { slug: "land-rover",    label: "Land Rover Blindado" },
  { slug: "volvo",         label: "Volvo Blindado" },
  { slug: "mercedes-benz", label: "Mercedes-Benz Blindado" },
  { slug: "honda",         label: "Honda Blindado" },
  { slug: "porsche",       label: "Porsche Blindado" },
  { slug: "byd",           label: "BYD Blindado" },
  { slug: "gwm",           label: "GWM Blindado" },
];

function Footer() {
  return (
    <footer className="border-t dark:border-white/5 border-black/5 py-10">
      <div className="mx-auto max-w-6xl px-6">
        <div className="hidden sm:flex flex-wrap justify-center gap-12">
          <div className="text-center">
            <p className="mb-3 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/50">Modelos</p>
            <ul className="columns-2 gap-6 space-y-2">
              {_SEO_MODELOS.map((m) => (
                <li key={m.slug}>
                  <Link
                    to="/blindados/modelo/$slug"
                    params={{ slug: m.slug }}
                    className="text-xs text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {m.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="text-center">
            <p className="mb-3 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/50">Marcas</p>
            <ul className="columns-2 gap-6 space-y-2">
              {_SEO_MARCAS.map((m) => (
                <li key={m.slug}>
                  <Link
                    to="/blindados/marca/$slug"
                    params={{ slug: m.slug }}
                    className="text-xs text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {m.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="flex flex-col items-center gap-4 sm:mt-8 sm:border-t sm:dark:border-white/5 sm:border-black/5 sm:pt-8">
          <nav className="flex flex-wrap justify-center gap-x-6 gap-y-1">
            <Link to="/blog" className="py-1 text-xs text-muted-foreground transition-colors hover:text-foreground">
              Blog
            </Link>
            <Link to="/tabela" className="py-1 text-xs text-muted-foreground transition-colors hover:text-foreground">
              Preços de mercado
            </Link>
            <Link to="/faq" className="py-1 text-xs text-muted-foreground transition-colors hover:text-foreground">
              Dúvidas
            </Link>
            <Link to="/termos" className="py-1 text-xs text-muted-foreground transition-colors hover:text-foreground">
              Termos
            </Link>
            <Link to="/privacidade" className="py-1 text-xs text-muted-foreground transition-colors hover:text-foreground">
              Privacidade
            </Link>
            <Link to="/contato" className="py-1 text-xs text-muted-foreground transition-colors hover:text-foreground">
              Contato
            </Link>
          </nav>
          <span className="text-xs text-muted-foreground/50">© 2026 Busca Blindados</span>
        </div>
      </div>
    </footer>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();
  const { session } = useSession();

  useEffect(() => {
    import("@/lib/turnstile").then(({ initTurnstile }) => initTurnstile());
  }, []);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    import("@/lib/posthog").then(({ posthog }) => {
      unsubscribe = router.subscribe("onResolved", () => {
        posthog.capture("$pageview");
      });
    });
    return () => unsubscribe?.();
  }, [router]);

  useEffect(() => {
    import("@/lib/posthog").then(({ posthog }) => {
      if (session) {
        posthog.startSessionRecording();
      } else {
        posthog.stopSessionRecording();
      }
    });
  }, [session]);

  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
      <Footer />
      <CookieConsentBanner />
    </QueryClientProvider>
  );
}
